import { useRef, useEffect, useLayoutEffect } from "react";
import type { Table } from "@tanstack/react-table";

export function useScrollPositionPreservation<T>(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  parentScrollContainerRef: React.RefObject<HTMLDivElement | null>,
  table: Table<T>,
  isLoading: boolean,
  dataLength: number,
  onScroll?: () => void
) {
  const savedScrollPositionRef = useRef<{
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  const prevSortingStateRef = useRef<string>(
    JSON.stringify(table.getState().sorting)
  );
  const needsRestoreRef = useRef(false);

  useEffect(() => {
    const innerContainer = scrollContainerRef.current;
    const parentContainer = parentScrollContainerRef.current;

    if (!innerContainer && !parentContainer) return;

    const handleScroll = () => {
      if (!needsRestoreRef.current && innerContainer) {
        savedScrollPositionRef.current = {
          scrollLeft: innerContainer.scrollLeft,
          scrollTop: innerContainer.scrollTop,
        };
      }
      onScroll?.();
    };

    if (innerContainer) {
      innerContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
    }
    if (parentContainer) {
      parentContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
    }

    return () => {
      if (innerContainer) {
        innerContainer.removeEventListener("scroll", handleScroll);
      }
      if (parentContainer) {
        parentContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [scrollContainerRef, parentScrollContainerRef]);

  const currentSorting = JSON.stringify(table.getState().sorting);
  useLayoutEffect(() => {
    const innerContainer = scrollContainerRef.current;
    const parentContainer = parentScrollContainerRef.current;

    if (
      currentSorting !== prevSortingStateRef.current &&
      (innerContainer || parentContainer)
    ) {
      needsRestoreRef.current = true;
    }

    prevSortingStateRef.current = currentSorting;
  }, [currentSorting, scrollContainerRef]);

  useLayoutEffect(() => {
    if (!needsRestoreRef.current || isLoading || dataLength === 0) {
      return;
    }

    const innerContainer = scrollContainerRef.current;
    const savedPosition = savedScrollPositionRef.current;

    if (!savedPosition || !innerContainer) {
      needsRestoreRef.current = false;
      return;
    }

    let restoreAttempts = 0;
    const maxAttempts = 10;

    const restoreAndCheck = () => {
      if (!innerContainer || !savedPosition) return false;

      if (savedPosition.scrollLeft !== undefined) {
        innerContainer.scrollLeft = savedPosition.scrollLeft;
      }
      if (savedPosition.scrollTop !== undefined) {
        innerContainer.scrollTop = savedPosition.scrollTop;
      }

      const leftMatches =
        Math.abs(innerContainer.scrollLeft - savedPosition.scrollLeft) < 1;
      const topMatches =
        Math.abs(innerContainer.scrollTop - savedPosition.scrollTop) < 1;

      return leftMatches && topMatches;
    };

    restoreAndCheck();

    const tryRestore = () => {
      restoreAttempts++;
      const success = restoreAndCheck();

      if (!success && restoreAttempts < maxAttempts) {
        requestAnimationFrame(tryRestore);
      } else {
        needsRestoreRef.current = false;
      }
    };

    requestAnimationFrame(tryRestore);
  }, [dataLength, isLoading, currentSorting, scrollContainerRef]);

  return { needsRestoreRef };
}
